import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, featuredArticles, newReleases, Article } from '@/mocks/movieData';

const allArticles = [...featuredArticles, ...newReleases];

function ArticleCard({ article }: { article: Article }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
    >
      <div className="relative overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full aspect-video object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {article.badge && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
            {article.badge}
          </span>
        )}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded text-accent-400 text-xs font-semibold">
          <i className="ri-star-fill text-xs"></i>
          {article.rating}
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-1">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
          {article.description}
        </p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-xs text-foreground-400">{article.date}</span>
          <span className="text-xs text-foreground-300">·</span>
          <span className="text-xs text-foreground-400">{article.views} 次浏览</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();

  const currentCategory = categories.find((c) => c.slug === type);
  const filteredArticles = allArticles.filter((a) => a.categorySlug === type);

  const categoryNameMap: Record<string, string> = {
    dianshiju: '电视剧',
    dianying: '电影',
    zongyi: '综艺',
    dongman: '动漫',
    jilupian: '纪录片',
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="WebPage" data={{
        name: currentCategory ? `${currentCategory.name} - 久久影视` : '分类 - 久久影视',
        description: currentCategory?.description || '久久影视分类浏览，包含最新电视剧更新、最新电影上映和最新综艺在线资源',
        inLanguage: 'zh-CN',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiuys.com' },
            { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类' },
          ],
        },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="mb-6">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {currentCategory?.name || categoryNameMap[type || ''] || '未知分类'}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base">
                  {currentCategory?.description || ''}
                </p>
              </div>

              <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      type === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无内容，敬请期待后续更新</p>
                </div>
              )}
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}